// Hideo - the references of a video (user request, 2/11/2026): the sources and saved videos linked to an idea or a
// video of the plan (video.sourceIds, video.savedIds), so research and examples sit with the video instead of in
// separate pages. In the video dialog (VideoRefs.vue): the linked items, "Link" (RefsPickDialog.vue: the items of the
// video's project, or of every project, with search) and a pasted link that becomes a new source or a new saved video
// at once (quickRef). The source and saved video dialogs show the videos that use them (refUsage).
import type { ProfileData, SavedVideo, Source, Video } from '../data/types'
import { normUrl } from '../utils/url'
import { pubDetect } from './published'
import { guessSourceKind, sourceDomain } from './sources'
import { matchesSaved } from './savedVideos'
import { matchesSource } from './sources'
import { ofProject } from './projects'

export type RefGroup = 'src' | 'saved'
/** The form fields that hold the references */
export interface RefLists { sourceIds: string[]; savedIds: string[] }
const KEY: Record<RefGroup, keyof RefLists> = { src: 'sourceIds', saved: 'savedIds' }

/** The linked items, in the order they were linked (ids of deleted items are skipped) */
export function linkedRefs(d: Pick<ProfileData, 'sources' | 'savedVideos'>, f: RefLists): { sources: Source[]; saved: SavedVideo[] } {
  return {
    sources: f.sourceIds.map(id => d.sources.find(s => s.id === id)).filter((s): s is Source => !!s),
    saved: f.savedIds.map(id => d.savedVideos.find(s => s.id === id)).filter((s): s is SavedVideo => !!s)
  }
}
export const isLinked = (f: RefLists, g: RefGroup, id: string): boolean => f[KEY[g]].includes(id)
/** Links or unlinks an item (the form's list changes in place) */
export function toggleRef(f: RefLists, g: RefGroup, id: string): void {
  const k = KEY[g]
  f[k] = f[k].includes(id) ? f[k].filter(x => x !== id) : [...f[k], id]
}

/** The items the picker offers: of one project ('all' = every project), matching the search; linked ones first */
export function refCandidates<T extends Source | SavedVideo>(list: T[], g: RefGroup, f: RefLists, projectId: string, query: string,
  plain: (text: string) => string): T[] {
  const q = query.trim().toLowerCase()
  const match = (x: T) => (g === 'src' ? matchesSource(x as Source, q, plain) : matchesSaved(x as SavedVideo, q, plain))
  return ofProject(list, projectId).filter(x => !q || match(x))
    .sort((a, b) => Number(isLinked(f, g, b.id)) - Number(isLinked(f, g, a.id)))
}

/** What a pasted link most likely is: a video on a social (saved video), otherwise a source; '' for something that is
 *  not a web address */
export function refKindOfLink(text: string): RefGroup | '' {
  const url = normUrl(text.trim())
  if (!/^https?:\/\/[^\s/]+\.[^\s/]+/i.test(url)) return ''
  const p = pubDetect(url)
  return p && p !== 'altro' && p !== 'linkedin' && p !== 'spotify' ? 'saved' : 'src'
}

/** A pasted link becomes a new item of the video's project and is linked to it: a source (title = its site, category
 *  guessed from the link) or a saved video. The id of the new item ('' for an empty link). The caller saves the data */
export function quickRef(d: ProfileData, f: RefLists, text: string, g: RefGroup, projectId: string, now = Date.now()): string {
  const url = normUrl(text.trim())
  if (!url) return ''
  let id: string
  if (g === 'src') {
    id = 's_' + now
    const s: Source = { id, title: '', tags: [], kind: guessSourceKind(url), projectId, url, thumbnail: '', note: '' }
    s.title = sourceDomain(s) || url
    d.sources.push(s)
  } else {
    id = 'sv_' + now
    d.savedVideos.push({ id, title: '', url, projectId, thumbnail: '', note: '', tags: [], kind: '', savedAt: now })
  }
  if (!isLinked(f, g, id)) toggleRef(f, g, id)
  return id
}

/** The videos that link an item (source or saved video dialog: "Used in") */
export const refUsage = (d: Pick<ProfileData, 'videos'>, g: RefGroup, id: string): Video[] =>
  d.videos.filter(v => (g === 'src' ? v.sourceIds : v.savedIds)?.includes(id))
